import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../auth'
import Layout  from '../components/Layout'
import { emailListApi } from '../api/client'

const C = { teal: '#2B4C5E', yellow: '#F2B705', orange: '#F5A800', muted: '#6B8A91', light: '#C7D9DB' }

interface EmailFile {
  key: string
  last_modified: string
}

export default function EmailListPage() {
  const { getToken, ready } = useAuth()
  const [files,     setFiles]     = useState<EmailFile[]>([])
  const [loading,   setLoading]   = useState(true)
  const [uploading, setUploading] = useState(false)
  const [dragOver,  setDragOver]  = useState(false)
  const [msg,       setMsg]       = useState('')
  const [isErr,     setIsErr]     = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => { if (ready) loadFiles() }, [ready])


  async function loadFiles() {
    try {
      await getToken()
      const r = await emailListApi.list()
      setFiles(r.data.files ?? [])
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  async function handleUpload(file: File) {
    const ok = file.name.endsWith('.xlsx') || file.name.endsWith('.csv')
    if (!ok) { flash('Only .xlsx or .csv files accepted', true); return }
    setUploading(true)
    try {
      await getToken()
      await emailListApi.upload(file)
      flash(`Uploaded: ${file.name}`)
      await loadFiles()
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Upload failed'
      flash(`Error: ${detail}`, true)
      console.error('Upload error:', e?.response?.data || e)
    }
    finally { setUploading(false) }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault(); setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleUpload(file)
  }

  function flash(text: string, err = false) {
    setIsErr(err); setMsg(text); setTimeout(() => setMsg(''), 5000)
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
  }

  return (
    <Layout>
      <main style={{ padding: '24px 20px', maxWidth: 800 }}>

        {msg && (
          <div style={{
            position: 'fixed', top: 80, right: 24,
            background: isErr ? '#7f1d1d' : C.teal,
            color: '#fff', padding: '12px 20px', borderRadius: 8,
            borderLeft: `4px solid ${isErr ? '#ef4444' : C.yellow}`,
            zIndex: 2000, maxWidth: 400,
          }}>{msg}</div>
        )}

        <div style={{ marginBottom: 32 }}>
          <h1 style={{ color: C.teal, fontSize: 24, fontWeight: 700 }}>Email Lists</h1>
          <p style={{ color: C.muted, fontSize: 14, marginTop: 4 }}>
            Upload recipient lists (.xlsx or .csv) used when sending report emails
          </p>
        </div>

        {/* Upload zone */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: 24,
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 24,
        }}>
          <div style={{
            color: C.teal, fontWeight: 700, fontSize: 14,
            marginBottom: 16, paddingBottom: 12,
            borderBottom: `2px solid ${C.yellow}`, display: 'inline-block',
          }}>Upload New List</div>

          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            style={{
              border:       `2px dashed ${dragOver ? C.yellow : '#d1d5db'}`,
              borderRadius: 10,
              padding:      '40px 24px',
              textAlign:    'center',
              cursor:       'pointer',
              background:   dragOver ? 'rgba(235,191,0,0.04)' : '#fafafa',
              transition:   'all 0.15s',
            }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📧</div>
            <div style={{ color: C.teal, fontWeight: 600 }}>
              {uploading ? 'Uploading…' : 'Drop file here or click to browse'}
            </div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
              .xlsx or .csv — first column should contain email addresses
            </div>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }}
              onChange={e => e.target.files?.[0] && handleUpload(e.target.files[0])} />
          </div>

          <div style={{
            background: 'rgba(235,191,0,0.06)', border: `1px solid rgba(235,191,0,0.25)`,
            borderRadius: 8, padding: '12px 16px', marginTop: 16,
            fontSize: 12, color: C.muted, lineHeight: 1.7,
          }}>
            <strong style={{ color: C.teal }}>File format:</strong> Excel (.xlsx) or CSV with a column named{' '}
            <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: 3 }}>Email</code>{' '}
            or <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: 3 }}>email_id</code>.
            The file is stored in S3 and referenced by scheduled reports.
          </div>
        </div>

        {/* Existing files */}
        <div style={{
          background: '#fff', borderRadius: 12, padding: 24,
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}>
          <div style={{
            color: C.teal, fontWeight: 700, fontSize: 14,
            marginBottom: 16, paddingBottom: 12,
            borderBottom: `2px solid ${C.yellow}`, display: 'inline-block',
          }}>Uploaded Lists</div>

          {loading ? (
            <div style={{ color: C.muted, textAlign: 'center', padding: '40px 0' }}>Loading…</div>
          ) : files.length === 0 ? (
            <div style={{ color: C.muted, textAlign: 'center', padding: '40px 0', fontSize: 14 }}>
              No email lists uploaded yet.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: C.muted, fontWeight: 600 }}>Filename</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: C.muted, fontWeight: 600 }}>S3 Key</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: C.muted, fontWeight: 600 }}>Last Modified</th>
                </tr>
              </thead>
              <tbody>
                {files.map(f => (
                  <tr key={f.key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px', color: C.teal, fontWeight: 600 }}>
                      📄 {f.key.split('/').pop()}
                    </td>
                    <td style={{ padding: '12px', color: C.muted, fontFamily: 'monospace', fontSize: 11 }}>
                      {f.key}
                    </td>
                    <td style={{ padding: '12px', color: C.muted }}>
                      {formatDate(f.last_modified)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </Layout>
  )
}
